import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  FlaskConical, TestTubes, ScanLine, Clock, Loader2, TrendingUp, TrendingDown,
  Minus, ChevronDown, ChevronRight, AlertTriangle, Check, MessageSquare,
  FileText, BarChart3, Calendar, List, Activity, CircleCheck, TriangleAlert,
  Eye, EyeOff, Send, Mail
} from 'lucide-react';

/**
 * LabDashboard — Full patient lab dashboard.
 * Shows all labs grouped by test type or date, with trending, communications history.
 */
export default function LabDashboard({ patientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('test_type');
  const [expandedGroup, setExpandedGroup] = useState(null);

  const loadData = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await api.get(`/labs/dashboard/${patientId}?group_by=${groupBy}`);
      setData(res.data);
      if (res.data.groups?.length && !expandedGroup) {
        setExpandedGroup(prev => prev || res.data.groups[0].group_key);
      }
    } catch (err) {
      console.error('Lab dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [patientId, groupBy]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  if (!data || data.groups?.length === 0) {
    return (
      <div className="text-center py-12">
        <FlaskConical className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
        <p className="text-sm text-muted-foreground">No lab results on file</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lab-dashboard">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-lg font-bold text-slate-800">{data.summary.total_results}</p>
            <p className="text-[9px] text-slate-400 uppercase">Results</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="text-center">
            <p className="text-lg font-bold text-amber-600">{data.summary.total_abnormal_values}</p>
            <p className="text-[9px] text-slate-400 uppercase">Abnormal</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="text-center">
            <p className="text-lg font-bold text-teal-600">{data.summary.analytes_with_trends}</p>
            <p className="text-[9px] text-slate-400 uppercase">Trending</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="text-center">
            <p className="text-lg font-bold text-blue-600">{data.summary.communications}</p>
            <p className="text-[9px] text-slate-400 uppercase">Comms</p>
          </div>
        </div>

        {/* Group by toggle */}
        <div className="flex p-0.5 rounded-lg bg-slate-100" data-testid="group-by-toggle">
          {[
            { key: 'test_type', icon: List, label: 'By Test' },
            { key: 'date', icon: Calendar, label: 'By Date' },
          ].map(opt => (
            <button key={opt.key} onClick={() => { setGroupBy(opt.key); setExpandedGroup(null); }}
              className={`flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-md transition-all ${
                groupBy === opt.key ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-400 hover:text-slate-600'
              }`} data-testid={`group-by-${opt.key}`}>
              <opt.icon className="w-3 h-3" />{opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-2">
        {data.groups.map(group => (
          <LabGroup
            key={group.group_key}
            group={group}
            trending={data.trending}
            communications={data.communications}
            expanded={expandedGroup === group.group_key}
            onToggle={() => setExpandedGroup(expandedGroup === group.group_key ? null : group.group_key)}
          />
        ))}
      </div>
    </div>
  );
}


function LabGroup({ group, trending, communications, expanded, onToggle }) {
  const [showComms, setShowComms] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const isImaging = group.item_type === 'imaging_result';
  const Icon = isImaging ? ScanLine : TestTubes;

  const borderColor = group.is_critical ? 'border-l-rose-400' : group.has_abnormal ? 'border-l-amber-400' : 'border-l-emerald-400';

  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm border-l-4 ${borderColor} transition-all`}
      data-testid={`lab-group-${group.group_key}`}>
      {/* Header */}
      <button onClick={onToggle} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50/50 transition-colors">
        <div className={`p-2 rounded-lg ${group.has_abnormal ? 'bg-amber-50' : 'bg-teal-50'}`}>
          <Icon className={`w-4 h-4 ${group.has_abnormal ? 'text-amber-500' : 'text-teal-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{group.title}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {group.result_count} result{group.result_count !== 1 ? 's' : ''}
            {group.latest_date && <> · Last: {formatDate(group.latest_date)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {Object.keys(group.trends || {}).length > 0 && (
            <Badge variant="outline" className="text-[9px] border-teal-200 text-teal-600 gap-0.5">
              <Activity className="w-2.5 h-2.5" />{Object.keys(group.trends).length} trending
            </Badge>
          )}
          {group.has_abnormal && (
            <Badge className="text-[9px] bg-amber-50 text-amber-600 border-amber-200" variant="outline">
              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />{group.total_abnormal}
            </Badge>
          )}
          {!group.has_abnormal && (
            <Badge className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200" variant="outline">
              <CircleCheck className="w-2.5 h-2.5 mr-0.5" />Normal
            </Badge>
          )}
          <Badge variant="outline" className={`text-[9px] ${
            group.latest_status === 'pending' ? 'border-amber-200 text-amber-600' : 'border-slate-200 text-slate-400'
          }`}>{group.latest_status}</Badge>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {/* Toolbar: Trends + Communications toggles */}
          <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-50/50">
            {Object.keys(group.trends || {}).length > 0 && (
              <button onClick={() => setShowTrends(!showTrends)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                  showTrends ? 'bg-teal-100 text-teal-700' : 'text-slate-500 hover:bg-slate-100'
                }`} data-testid={`trends-toggle-${group.group_key}`}>
                <BarChart3 className="w-3 h-3" />Trends
              </button>
            )}
            {communications.length > 0 && (
              <button onClick={() => setShowComms(!showComms)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                  showComms ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-100'
                }`} data-testid={`comms-toggle-${group.group_key}`}>
                <MessageSquare className="w-3 h-3" />Communications ({communications.length})
              </button>
            )}
          </div>

          {/* Trends panel */}
          {showTrends && Object.keys(group.trends || {}).length > 0 && (
            <div className="px-4 pb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                {Object.entries(group.trends).map(([analyte, info]) => (
                  <TrendCard key={analyte} analyte={analyte} data={info} />
                ))}
              </div>
            </div>
          )}

          {/* Communications panel */}
          {showComms && communications.length > 0 && (
            <div className="px-4 pb-3">
              <div className="space-y-2 mt-2">
                {communications.map((comm, i) => (
                  <CommCard key={comm.id || i} comm={comm} />
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          <div className="divide-y divide-slate-50">
            {group.results.map(result => (
              <ResultRow key={result.id} result={result} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ResultRow({ result }) {
  const [expanded, setExpanded] = useState(false);
  const isImaging = result.item_type === 'imaging_result';
  const Icon = isImaging ? ScanLine : TestTubes;

  return (
    <div className="px-4 py-2.5">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left flex items-center gap-3">
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${result.abnormal_count > 0 ? 'text-amber-500' : 'text-teal-500'}`} />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium text-slate-700">{result.title}</span>
          {result.abnormal_list?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {result.abnormal_list.slice(0, 4).map((flag, i) => (
                <span key={i} className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                  flag.includes('High') ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-600'
                }`}>{flag}</span>
              ))}
              {result.abnormal_list.length > 4 && <span className="text-[9px] text-slate-400">+{result.abnormal_list.length - 4}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] text-slate-400">{formatDate(result.created_at)}</span>
          <Badge variant="outline" className={`text-[8px] ${
            result.status === 'pending' ? 'border-amber-200 text-amber-600' : 'border-slate-200 text-slate-400'
          }`}>{result.status}</Badge>
          <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="mt-2 ml-7">
          {result.parsed_values?.length > 0 ? (
            <table className="w-full text-xs border border-slate-100 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="text-left px-3 py-1.5 font-medium">Analyte</th>
                  <th className="text-right px-3 py-1.5 font-medium">Value</th>
                  <th className="text-center px-3 py-1.5 font-medium">Flag</th>
                  <th className="text-left px-3 py-1.5 font-medium">Reference</th>
                </tr>
              </thead>
              <tbody>
                {result.parsed_values.map((val, i) => (
                  <tr key={i} className={`border-t border-slate-50 ${val.is_abnormal ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-3 py-1.5 text-slate-700 font-medium">{val.analyte}</td>
                    <td className={`px-3 py-1.5 text-right font-mono font-bold ${
                      val.flag === 'H' ? 'text-rose-600' : val.flag === 'L' ? 'text-blue-600' : 'text-slate-700'
                    }`}>{val.value}{val.unit_suffix}</td>
                    <td className="px-3 py-1.5 text-center">
                      {val.flag === 'H' && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-100 text-rose-600 text-[9px] font-bold">H</span>}
                      {val.flag === 'L' && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[9px] font-bold">L</span>}
                      {!val.flag && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50 text-emerald-500"><Check className="w-3 h-3" /></span>}
                    </td>
                    <td className="px-3 py-1.5 text-slate-400">{val.reference_range}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600 whitespace-pre-wrap">
              {result.body}
            </div>
          )}
          {result.provider_response && (
            <div className="mt-2 rounded-lg bg-blue-50/50 border border-blue-100 px-3 py-2">
              <p className="text-[9px] font-semibold text-blue-500 mb-0.5">Provider Response</p>
              <p className="text-xs text-slate-700">{result.provider_response}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TrendCard({ analyte, data }) {
  const { datapoints, direction, latest, previous } = data;
  const isUp = direction === 'up';
  const lastFlag = datapoints[datapoints.length - 1]?.flag;
  const isAbnormal = lastFlag === 'H' || lastFlag === 'L';
  const changePct = previous ? (((latest - previous) / previous) * 100).toFixed(1) : 0;

  const values = datapoints.map(dp => dp.value);
  const minVal = Math.min(...values) * 0.9;
  const maxVal = Math.max(...values) * 1.1;
  const range = maxVal - minVal || 1;
  const sparkPoints = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * 80 + 10;
    const y = 40 - ((v - minVal) / range) * 30 + 5;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${isAbnormal ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100 bg-slate-50/50'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold text-slate-700 truncate">{analyte}</span>
        <div className="flex items-center gap-1">
          {isUp ? <TrendingUp className={`w-3 h-3 ${isAbnormal ? 'text-rose-500' : 'text-slate-400'}`} /> :
           direction === 'down' ? <TrendingDown className="w-3 h-3 text-emerald-500" /> :
           <Minus className="w-3 h-3 text-slate-400" />}
          <span className={`text-[10px] font-medium ${isUp && isAbnormal ? 'text-rose-600' : 'text-slate-500'}`}>
            {changePct > 0 ? '+' : ''}{changePct}%
          </span>
        </div>
      </div>
      <div className="h-10 w-full">
        <svg viewBox="0 0 100 45" className="w-full h-full" preserveAspectRatio="none">
          <polyline points={sparkPoints} fill="none" stroke={isAbnormal ? '#f59e0b' : '#94a3b8'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {values.map((v, i) => {
            const x = (i / Math.max(values.length - 1, 1)) * 80 + 10;
            const y = 40 - ((v - minVal) / range) * 30 + 5;
            const isLast = i === values.length - 1;
            return <circle key={i} cx={x} cy={y} r={isLast ? 3 : 2}
              fill={isLast ? (isAbnormal ? '#ef4444' : '#3b82f6') : (isAbnormal ? '#fbbf24' : '#cbd5e1')}
              stroke="white" strokeWidth="1" />;
          })}
        </svg>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[9px] text-slate-400">{datapoints.length} readings</span>
        <span className={`text-xs font-bold ${isAbnormal ? 'text-amber-700' : 'text-slate-700'}`}>{latest}</span>
      </div>
      {/* Value history */}
      <div className="mt-1.5 space-y-0.5">
        {datapoints.map((dp, i) => (
          <div key={i} className="flex items-center justify-between text-[9px]">
            <span className="text-slate-400">{formatDate(dp.date)}</span>
            <span className={`font-mono font-medium ${dp.flag === 'H' ? 'text-rose-600' : dp.flag === 'L' ? 'text-blue-600' : 'text-slate-600'}`}>
              {dp.value} {dp.flag && <span className="text-[8px]">({dp.flag})</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


function CommCard({ comm }) {
  const [expanded, setExpanded] = useState(false);

  if (comm.type === 'letter') {
    return (
      <div className="rounded-lg border border-orange-100 bg-orange-50/20 px-3 py-2">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-orange-500" />
          <span className="text-[11px] font-medium text-slate-700 flex-1">
            Patient Letter ({comm.letter_type?.replace(/_/g, ' ')})
          </span>
          <Badge variant="outline" className={`text-[8px] ${
            comm.status === 'sent' ? 'border-emerald-200 text-emerald-600' : 'border-slate-200 text-slate-400'
          }`}>{comm.status}</Badge>
          <span className="text-[9px] text-slate-400">{formatDate(comm.date)}</span>
        </div>
        {comm.content_preview && (
          <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">{comm.content_preview}</p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/20 px-3 py-2">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-[11px] font-medium text-slate-700 flex-1 truncate">{comm.title}</span>
        <Badge variant="outline" className={`text-[8px] ${
          comm.status === 'resolved' ? 'border-emerald-200 text-emerald-600' : 'border-amber-200 text-amber-600'
        }`}>{comm.status}</Badge>
        <span className="text-[9px] text-slate-400">{formatDate(comm.date)}</span>
        <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && comm.messages?.length > 0 && (
        <div className="mt-2 space-y-1.5 pl-5">
          {comm.messages.map((msg, i) => (
            <div key={i} className={`rounded-lg px-2.5 py-1.5 text-[10px] ${
              msg.role === 'provider' ? 'bg-slate-100 ml-4' : 'bg-blue-50 mr-4'
            }`}>
              <div className="flex items-center gap-1 mb-0.5">
                <span className="font-semibold text-[9px]">{msg.sender}</span>
                <span className="text-[8px] text-slate-400">{formatDate(msg.timestamp)}</span>
              </div>
              <p className="text-slate-700 leading-relaxed">{msg.body}</p>
            </div>
          ))}
          {comm.action_request && (
            <div className="rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-1.5 text-[10px]">
              <span className="font-semibold text-amber-700">Action: </span>
              <span className="text-amber-600">{comm.action_request.text}</span>
              <span className={`ml-2 text-[8px] font-medium ${
                comm.action_request.status === 'completed' ? 'text-emerald-600' : 'text-amber-500'
              }`}>({comm.action_request.status})</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function formatDate(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch { return ''; }
}
